import { useState, useEffect, useCallback } from 'react';
import { notificationService } from '../services/notificationService';
import { 
  saveUserToken, 
  removeUserToken, 
  saveNotificationPreferences, 
  getNotificationPreferences 
} from '../firebase/notifications';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook personalizado para gerenciar notificações push
 * @returns {Object} Objeto com estados e funções para gerenciar notificações
 */
export const useNotifications = () => {
  const { currentUser } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('unknown');
  const [token, setToken] = useState(null);
  const [preferences, setPreferences] = useState({
    newAppointments: true,
    canceledAppointments: true,
    rescheduledAppointments: true,
    appointmentReminders: true
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Inicializa o hook, verificando se notificações são suportadas e o status de permissão
   */
  const init = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar se notificações são suportadas
      const supported = await notificationService.isNotificationSupported();
      setIsSupported(supported);

      if (!supported) {
        setPermissionStatus('unsupported');
        setLoading(false);
        return;
      }

      // Verificar status de permissão
      const status = notificationService.getNotificationPermissionStatus();
      setPermissionStatus(status);

      // Inicializar o serviço de notificações
      await notificationService.init();

      // Carregar preferências de notificação do Firestore
      if (currentUser?.uid) {
        const savedPreferences = await getNotificationPreferences(currentUser.uid);
        if (savedPreferences) {
          setPreferences(savedPreferences);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Erro ao inicializar notificações:', error);
      setError('Não foi possível inicializar as notificações.');
      setLoading(false);
    }
  }, [currentUser?.uid]);

  /**
   * Solicita permissão para enviar notificações push
   * @returns {Promise<boolean>} Indica se a permissão foi concedida
   */
  const requestPermission = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!isSupported) {
        setError('Este dispositivo não suporta notificações push.');
        setLoading(false);
        return false;
      }

      // Solicitar permissão e obter token
      const fcmToken = await notificationService.requestPermissionAndGetToken();
      
      // Atualizar estado de permissão
      const newStatus = notificationService.getNotificationPermissionStatus();
      setPermissionStatus(newStatus);
      
      if (fcmToken) {
        setToken(fcmToken);
        
        // Salvar token no Firestore
        if (currentUser?.uid) {
          await saveUserToken(currentUser.uid, fcmToken);
        }
        
        setLoading(false);
        return true;
      } else {
        setLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
      setError('Não foi possível solicitar permissão para notificações.');
      setLoading(false);
      return false;
    }
  }, [isSupported, currentUser?.uid]);

  /**
   * Atualiza as preferências de notificação
   * @param {Object} newPreferences Novas preferências
   * @returns {Promise<boolean>} Indica se a operação foi bem-sucedida
   */
  const updatePreferences = useCallback(async (newPreferences) => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser?.uid) {
        setError('Usuário não autenticado.');
        setLoading(false);
        return false;
      }

      // Salvar preferências no Firestore
      const success = await saveNotificationPreferences(currentUser.uid, newPreferences);
      
      if (success) {
        setPreferences(newPreferences);
        setLoading(false);
        return true;
      } else {
        setError('Não foi possível salvar as preferências.');
        setLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Erro ao atualizar preferências:', error);
      setError('Não foi possível atualizar as preferências.');
      setLoading(false);
      return false;
    }
  }, [currentUser?.uid]);

  /**
   * Registra callback para notificações em foreground
   * @param {Function} callback Função a ser chamada quando uma notificação for recebida
   */
  const onNotificationReceived = useCallback((callback) => {
    notificationService.onMessageReceived(callback);
  }, []);

  // Inicializar o hook ao montar o componente ou quando o usuário mudar
  useEffect(() => {
    init();
    
    // Cleanup: remover token quando o componente for desmontado
    return () => {
      if (token && currentUser?.uid) {
        removeUserToken(token).catch(console.error);
      }
    };
  }, [init, token, currentUser?.uid]);

  return {
    isSupported,
    permissionStatus,
    token,
    preferences,
    loading,
    error,
    requestPermission,
    updatePreferences,
    onNotificationReceived
  };
}; 